<script>
/* global Sentry */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import PipelinesAPI from 'dashboard/api/pipelines';

export default {
  name: 'PipelineSection',
  data() {
    return {
      // NOOVI (cliente 2026-07-03): uma conversa/lead pode estar em VÁRIOS
      // pipelines ao mesmo tempo. Guardamos TODOS os cards vinculados, não só o
      // primeiro — o painel lista cada card com seus próprios controles.
      pipelineCards: [],
      pipelines: [],
      loading: false,
      showAddForm: false,
      selectedPipelineId: null,
      selectedStage: null,
      creatingCard: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
    currentConversationId() {
      // In the frontend store, 'id' IS the display_id (conversations are indexed by display_id)
      return this.currentChat?.id || null;
    },
    hasPipelineCards() {
      return this.pipelineCards.length > 0;
    },
    selectedPipeline() {
      if (!this.selectedPipelineId || !this.pipelines.length) return null;
      return this.pipelines.find(f => f.id === this.selectedPipelineId);
    },
    // Pipelines em que a conversa AINDA não tem card. Evita criar um segundo
    // card no mesmo pipeline (o backend não tem restrição de unicidade).
    availablePipelines() {
      const linkedIds = new Set(this.pipelineCards.map(c => c.pipeline_id));
      return this.pipelines.filter(p => !linkedIds.has(p.id));
    },
    contact() {
      return this.currentChat?.meta?.sender || {};
    },
  },
  watch: {
    currentConversationId: {
      handler() {
        this.fetchPipelineData();
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchPipelineData();
  },
  methods: {
    async fetchPipelineData() {
      if (!this.currentConversationId) return;

      this.loading = true;
      try {
        const [pipelinesResponse, pipelineCardResponse] = await Promise.all([
          PipelinesAPI.get(),
          PipelineCardsAPI.getCardByConversationId(this.currentConversationId),
        ]);

        this.pipelines = pipelinesResponse.data || [];
        this.pipelineCards = pipelineCardResponse.data || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pipeline data:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
      } finally {
        this.loading = false;
      }
    },
    pipelineForCard(card) {
      if (!card?.pipeline_id || !this.pipelines.length) return null;
      return this.pipelines.find(p => p.id === card.pipeline_id);
    },
    stageLabelForCard(card) {
      const pipeline = this.pipelineForCard(card);
      if (!pipeline?.stages) return card?.pipeline_stage || null;
      return pipeline.stages[card.pipeline_stage]?.name || card.pipeline_stage;
    },
    stageColorForCard(card) {
      const pipeline = this.pipelineForCard(card);
      return pipeline?.stages?.[card.pipeline_stage]?.color || '#64748B';
    },
    generatePipelineSlug(pipeline) {
      if (!pipeline) return '';
      const name = pipeline.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      return `${pipeline.id}-${name}`;
    },
    goToPipeline(card) {
      const pipeline = this.pipelineForCard(card);
      if (!card || !pipeline) return;

      this.$router.push({
        name: 'pipeline_item_detail',
        params: {
          accountId: this.$route.params.accountId,
          pipelineSlug: this.generatePipelineSlug(pipeline),
          view: 'item',
          itemId: card.id,
        },
      });
    },
    async moveToStage(card, newStage) {
      if (!card || newStage === card.pipeline_stage) return;

      try {
        const updatedCard = await PipelineCardsAPI.moveToStage(
          card.id,
          newStage
        );
        this.replaceCard(
          card.id,
          updatedCard || { ...card, pipeline_stage: newStage }
        );
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.MOVED_SUCCESS'));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error moving to stage:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.MOVED_ERROR'));
      }
    },
    getFirstStageByPosition(stages) {
      const stagesObj = stages || {};
      const sortedStages = Object.entries(stagesObj)
        .map(([key, value]) => ({ key, ...value }))
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      return (
        sortedStages.find(s => !s.is_won_stage && !s.is_lost_stage)?.key ||
        sortedStages[0]?.key ||
        'lead'
      );
    },
    async changePipeline(card, newPipelineId) {
      const pipelineId = Number(newPipelineId);
      if (!card || pipelineId === card.pipeline_id) return;

      const newPipeline = this.pipelines.find(p => p.id === pipelineId);
      if (!newPipeline) return;

      const firstStage = this.getFirstStageByPosition(newPipeline.stages);

      try {
        await PipelineCardsAPI.updateCard(card.id, {
          pipeline_id: pipelineId,
          pipeline_stage: firstStage,
        });
        this.replaceCard(card.id, {
          ...card,
          pipeline_id: pipelineId,
          pipeline_stage: firstStage,
        });
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(
          this.$t('CONTACT_PANEL.PIPELINE_SECTION.PIPELINE_CHANGED_SUCCESS')
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error changing pipeline:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
        useAlert(
          this.$t('CONTACT_PANEL.PIPELINE_SECTION.PIPELINE_CHANGED_ERROR')
        );
      }
    },
    async removeFromPipeline(card) {
      if (!card) return;

      try {
        await PipelineCardsAPI.deleteCard(card.id);
        this.pipelineCards = this.pipelineCards.filter(c => c.id !== card.id);
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVED_SUCCESS'));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error removing from pipeline:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVED_ERROR'));
      }
    },
    replaceCard(cardId, updatedCard) {
      const index = this.pipelineCards.findIndex(c => c.id === cardId);
      if (index !== -1) {
        this.pipelineCards.splice(index, 1, updatedCard);
      }
    },
    toggleAddForm() {
      this.showAddForm = !this.showAddForm;
      if (this.showAddForm && this.availablePipelines.length > 0) {
        this.selectedPipelineId = this.availablePipelines[0].id;
        this.updateDefaultStage();
      }
    },
    updateDefaultStage() {
      if (this.selectedPipeline?.stages) {
        this.selectedStage = this.getFirstStageByPosition(
          this.selectedPipeline.stages
        );
      }
    },
    async createPipelineCard() {
      if (
        !this.selectedPipelineId ||
        !this.selectedStage ||
        !this.currentConversationId
      )
        return;

      this.creatingCard = true;
      try {
        const cardData = {
          pipeline_id: this.selectedPipelineId,
          pipeline_stage: this.selectedStage,
          conversation_display_id: this.currentConversationId,
          item_details: {
            title: this.contact?.name || 'Nova conversa',
            conversation_id: this.currentConversationId,
          },
        };

        const response = await PipelineCardsAPI.createCard(cardData);
        if (response.data) {
          this.pipelineCards.push(response.data);
        }
        this.showAddForm = false;
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.ADDED_SUCCESS'));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating pipeline card:', error);
        // Send to Sentry
        if (window.Sentry) {
          Sentry.withScope(scope => {
            scope.setContext('pipeline_card', {
              pipeline_id: this.selectedPipelineId,
              stage: this.selectedStage,
              conversation_id: this.currentConversationId,
            });
            scope.setExtra('error_response', error.response?.data);
            Sentry.captureException(error);
          });
        }
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.ADDED_ERROR'));
      } finally {
        this.creatingCard = false;
      }
    },
  },
};
</script>

<template>
  <div class="py-2">
    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center py-4">
      <span class="i-lucide-loader-2 animate-spin text-n-slate-10" />
    </div>

    <!-- Pipeline cards (0..N) + shared add form -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="card in pipelineCards"
        :key="card.id"
        class="flex flex-col gap-3 pb-3 border-0 border-b border-solid border-n-weak last:border-b-0 last:pb-0"
      >
        <!-- Pipeline and Stage badges -->
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="px-2 py-1 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
          >
            {{
              pipelineForCard(card)?.name ||
              $t('CONTACT_PANEL.PIPELINE_SECTION.NO_PIPELINE')
            }}
          </span>
          <span
            v-if="stageLabelForCard(card)"
            class="px-2 py-1 text-xs font-medium rounded-full"
            :style="{
              backgroundColor: `${stageColorForCard(card)}20`,
              color: stageColorForCard(card),
            }"
          >
            {{ stageLabelForCard(card) }}
          </span>
        </div>

        <!-- Change Pipeline (if multiple pipelines) -->
        <div v-if="pipelines.length > 1" class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-10">
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.CHANGE_PIPELINE') }}
          </span>
          <select
            class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :value="card.pipeline_id"
            @change="changePipeline(card, $event.target.value)"
          >
            <option
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              :value="pipeline.id"
            >
              {{ pipeline.name }}
            </option>
          </select>
        </div>

        <!-- Move to stage -->
        <div v-if="pipelineForCard(card)?.stages" class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-10">
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.MOVE_STAGE') }}
          </span>
          <select
            class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :value="card.pipeline_stage"
            @change="moveToStage(card, $event.target.value)"
          >
            <option
              v-for="(stage, key) in pipelineForCard(card).stages"
              :key="key"
              :value="key"
            >
              {{ stage.name }}
            </option>
          </select>
        </div>

        <!-- Action buttons -->
        <div class="flex gap-1.5 mt-1">
          <button
            class="flex-1 inline-flex items-center justify-center gap-2 h-8 px-3 text-sm rounded-lg border-0 outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10 transition-all duration-200"
            @click="goToPipeline(card)"
          >
            <span class="i-lucide-external-link flex-shrink-0" />
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.VIEW_ITEM') }}
          </button>
          <button
            v-tooltip.top="$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
            class="inline-flex items-center justify-center h-8 w-8 rounded-lg border-0 outline outline-1 outline-n-ruby-8 text-n-ruby-11 hover:bg-n-ruby-9/10 transition-all duration-200"
            @click="removeFromPipeline(card)"
          >
            <span class="i-lucide-trash-2 flex-shrink-0" />
          </button>
        </div>
      </div>

      <!-- Empty hint (no cards, not adding) -->
      <p
        v-if="!hasPipelineCards && !showAddForm"
        class="text-xs text-n-slate-10 m-0"
      >
        {{ $t('CONTACT_PANEL.PIPELINE_SECTION.NO_ITEM') }}
      </p>

      <!-- Add button: first card OR another pipeline (shared). Hidden when the
           conversation already has a card in every pipeline. -->
      <button
        v-if="!showAddForm && availablePipelines.length > 0"
        class="w-full inline-flex items-center justify-center gap-2 h-8 px-3 text-sm rounded-lg border-0 outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10 transition-all duration-200"
        @click="toggleAddForm"
      >
        <span class="i-lucide-plus flex-shrink-0" />
        {{
          hasPipelineCards
            ? $t('CONTACT_PANEL.PIPELINE_SECTION.ADD_ANOTHER_PIPELINE')
            : $t('CONTACT_PANEL.PIPELINE_SECTION.ADD_TO_PIPELINE')
        }}
      </button>
      <p
        v-else-if="!showAddForm && !pipelines.length"
        class="text-xs text-n-slate-10 m-0"
      >
        {{ $t('CONTACT_PANEL.PIPELINE_SECTION.NO_PIPELINES_AVAILABLE') }}
      </p>

      <!-- Add form (shared: first card or additional pipeline) -->
      <div v-if="showAddForm" class="flex flex-col gap-3">
        <!-- Select Pipeline -->
        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-10">
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_PIPELINE') }}
          </span>
          <select
            v-model="selectedPipelineId"
            class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="updateDefaultStage"
          >
            <option
              v-for="pipeline in availablePipelines"
              :key="pipeline.id"
              :value="pipeline.id"
            >
              {{ pipeline.name }}
            </option>
          </select>
        </div>

        <!-- Select Stage -->
        <div v-if="selectedPipeline?.stages" class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-10">
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_STAGE') }}
          </span>
          <select
            v-model="selectedStage"
            class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-alpha-1 text-n-slate-12"
          >
            <option
              v-for="(stage, key) in selectedPipeline.stages"
              :key="key"
              :value="key"
            >
              {{ stage.name }}
            </option>
          </select>
        </div>

        <!-- Buttons -->
        <div class="flex gap-1.5">
          <button
            class="flex-1 inline-flex items-center justify-center h-8 px-3 text-sm rounded-lg border-0 outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10 transition-all duration-200"
            @click="toggleAddForm"
          >
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.CANCEL') }}
          </button>
          <button
            class="flex-1 inline-flex items-center justify-center gap-2 h-8 px-3 text-sm rounded-lg border-0 bg-n-brand text-white hover:brightness-110 transition-all duration-200 disabled:opacity-50"
            :disabled="!selectedPipelineId || !selectedStage || creatingCard"
            @click="createPipelineCard"
          >
            <span v-if="creatingCard" class="i-lucide-loader-2 animate-spin" />
            <span v-else class="i-lucide-check" />
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.ADD') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
